import { InjectionToken } from '@angular/core';
import {
  ORG_DEACTIVATE_DIALOG,
  organizationContactState,
  OrganizationContextType,
  OrganizationDetailSignalStoreProps,
  OrganizationErrors,
  OrganizationLoadingState,
  OrganizationSignalState,
  OrganizationState,
} from '../types';
import {
  AddressNew,
  OrganizationsResponseBody,
  OrganizationTypesResponseBody,
  Paged,
} from '@erm/cluster';

export const DEFAULT_ORG_ROUTES = [
  {
    name: 'Overview',
    path: 'overview', // or just 'overview' for relative routing
  },
  {
    name: 'Studies',
    path: 'studies',
  },
  {
    name: 'Sites',
    path: 'sites',
  },
];

export const organizationInitialState: OrganizationState = {
  countriesTotal: 0,
  data: {
    addresses: [],
    createdDt: '',
    data: {},
    id: '',
    invoiceEmails: [],
    isActive: false,
    name: '',
    parentId: null,
    purchaseOrders: [],
    updatedDt: '',
  },
  error: null,
  loading: true,
  orgNameExists: false,
  statesTotal: 0,
};

export const ORGANIZATION_DETAIL_STATE = new InjectionToken<OrganizationState>(
  'Organization Detail State',
  {
    factory: () => ({ ...organizationInitialState }),
  },
);

export const DEACTIVATE_CONFIRM: ORG_DEACTIVATE_DIALOG =
  constructDeactivateDialogData(true);

export const ORGANIZATION_UNABLE_TO_DEACTIVATE: ORG_DEACTIVATE_DIALOG =
  constructDeactivateDialogData(false);

export function _constructAddressString(address: AddressNew) {
  const _address = [
    address.address1,
    address.address2,
    address.address3,
  ].reduce((acc, _adress) => {
    if (_adress?.length > 0) {
      acc += `${_adress}, `;
    }
    return acc;
  }, '');
  return `${_address} ${address.city}, ${address.state}, ${address.zipCode}`;
}

export function constructDeactivateDialogData(
  canDeactivate: boolean,
): ORG_DEACTIVATE_DIALOG {
  return {
    buttons: {
      cancelButtonText: canDeactivate ? 'Cancel' : '',
      cancelColor: 'tertiary',
      deleteButtonText: canDeactivate ? 'Deactivate' : 'Done',
      primaryColor: canDeactivate ? 'primary-warn' : 'primary',
    },
    description: canDeactivate
      ? 'Are you sure you want to deactivate this organization? This action will also deactivate all user profiles.'
      : 'The organization cannot be deactivated while it has active studies or sites. To review them please navigate to the Studies or Sites tab on the organization page.',
    title: 'Request to deactivate',
    type: 'MESSAGE',
  };
}

export const removeBillingAddressDialogData = (
  address: AddressNew,
  canRemove: boolean,
): ORG_DEACTIVATE_DIALOG => {
  const _description = canRemove
    ? `Are you sure you want to remove ${_constructAddressString(address)}?`
    : `${_constructAddressString(address)} is a primary address. Make another address a primary address before removing this address`;

  return {
    buttons: {
      cancelButtonText: canRemove ? 'Cancel' : '',
      cancelColor: 'tertiary',
      deleteButtonText: canRemove ? 'Remove' : 'Okay',
      primaryColor: canRemove ? 'primary-warn' : 'primary',
    },
    description: _description,
    title: 'Remove Billing Address',
    type: 'MESSAGE',
  };
};

/**
 * @NOTE: ORGANIZATION V2
 *
 */
export const ORG_DEFAULT_PAGE = <T extends object>(): Paged<T> => ({
  page: 0,
  results: [],
  size: 10,
  total: 0,
});
export const ORGANIZATION_DEFAULT_SIGNAL_STATE: OrganizationSignalState = {
  organizations: {
    data: ORG_DEFAULT_PAGE<OrganizationsResponseBody>(),
  },
  organizationTypes: {
    data: ORG_DEFAULT_PAGE<OrganizationTypesResponseBody>(),
  },
  error: null,
  loading: null,
};

export const setOrganizationPageError = (
  type: OrganizationContextType,
  error: Error | null,
): OrganizationErrors => {
  return {
    type: type,
    error: error,
  };
};

export const setOrgLoadingState = (
  type: OrganizationContextType,
  isLoading: boolean,
): OrganizationLoadingState => {
  return {
    type: type,
    isLoading,
  };
};

export const createOrganizationDetailDefaultSignalStore =
  (): OrganizationDetailSignalStoreProps => ({
    organization: null,
    organizationAddresses: null,
    organizationKey: '',
    organizationMembers: [],
    membersLoading: new Set(),
    membersError: null,
    loading: null,
    error: null,
  });

export const organizationDetailDefaultSignalStore =
  createOrganizationDetailDefaultSignalStore();

export const ORGANIZATION_DETAIL_DEFAULT_SIGNAL_STORE =
  new InjectionToken<OrganizationDetailSignalStoreProps>(
    'Organization Detail Signal Store',
    {
      factory: () => createOrganizationDetailDefaultSignalStore(),
    },
  );

/// Organization Contact Constants

// Initial State and Token for Organization Contact Signal Store
export const organizationContactInitialState: organizationContactState = {
  data: [],
  readUrl: '',
  writeUrl: '',
  rolesUrl: '',
  entityId: '',
  exportUrl: '',
  exportArgs: {},
  searchText: '',
  entityType: 'study',
  organizationRoles: [],
  organizationNameById: {},
  organizationIrbAffiliatedById: {},
  loading: false,
  isRequestInProgress: false,
  error: null,
  canCloseDialog: true,
};

// Injection token for the initial state of the organization contact signal store, providing a default value for the state when the store is initialized.
export const ORGANIZATION_CONTACT_INITIAL_SIGNAL_STATE =
  new InjectionToken<organizationContactState>(
    'Organization Contact Signal State',
    {
      factory: () => ({ ...organizationContactInitialState }),
    },
  );
